import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import {
  B2CloudDownloadModalState,
  BookmarkPatientListModalState,
  CancelDraftAppointmentPaneState,
  ChargeModalState,
  CheckinEntryProfileDrawerState,
  CheckinFinishConfirmModalModalState,
  CheckinNotifyConfirmModalState,
  DraftAppointmentPatientProfileDrawerState,
  FinishDraftAppointmentModalState,
  InvitationHistoryPageInfoState,
  InvitationModal,
  LineVisitorFileState,
  LineVisitorHistoryPageInfo,
  LineVisitorProfileDrawerState,
  LineVisitorTab,
  NotificationConfirmModalState,
  NotificationCongestionModalState,
  OutpatientQuestionnaireEntryFinishConfirmModalModalState,
  OutpatientQuestionnaireEntryProfileDrawerState,
  OutpatientQuestionnaireEntrySendUnavailableModalState,
  OutpatientQuestionnaireNotifyConfirmModalState,
  ReceptionPageInfo,
  RequestAppointmentModalState,
  RequestUberDeliveryModalState,
  SendUnavailableModalState,
  WebVisitorFileState,
  WebVisitorProfileDrawerState,
  WebVisitorTab,
} from './types';

export const b2CloudDownloadModalState = atomWithReset<B2CloudDownloadModalState>({
  isOpen: false,
  disabled: false,
  error: null,
  appointmentIds: [],
});

export const bookmarkPatientListModalState = atomWithReset<BookmarkPatientListModalState>({
  isOpen: false,
  disabled: false,
  error: null,
  page: 1,
  totalPage: 1,
  perPage: 25,
  yearMonth: null,
  patientCount: 0,
});

export const checkinFinishConfirmModalModalState =
  atomWithReset<CheckinFinishConfirmModalModalState>({
    isOpen: false,
    checkinEntryId: null,
  });

export const checkinNotifyConfirmModalState = atomWithReset<CheckinNotifyConfirmModalState>({
  isOpen: false,
  checkinEntryId: null,
});

export const receptionPageInfoState = atom<ReceptionPageInfo>({
  page: 1,
  totalPage: 1,
  perPage: 25,
  orderBy: { field: 'start', direction: 'asc' },
  date: new Date(),
  statuses: [],
  patientName: null,
});

export const invitationModalState = atomWithReset<InvitationModal>({
  isOpen: false,
  invitationCode: '',
  invitationCodeId: '',
  phoneNumber: '',
  memo: '',
});

export const invitationHistoryPageInfoState = atom<InvitationHistoryPageInfoState>({
  isSendFailed: true,
  isNotApplied: true,
  page: 1,
  totalPage: 1,
  perPage: 25,
  error: null,
});

export const lineVisitorProfileDrawerState = atomWithReset<LineVisitorProfileDrawerState>({
  isOpen: false,
  lineVisitorId: null,
  appointmentId: null,
});

export const lineVisitorTabState = atom<LineVisitorTab>({
  type: 'reception',
});

export const lineVisitorFileState = atom<LineVisitorFileState>({
  lineVisitorId: null,
});

export const lineVisitorHistoryPageInfoState = atom<LineVisitorHistoryPageInfo>({
  lineVisitorId: null,
  page: 1,
  totalPage: 1,
  perPage: 15,
});

export const lineVisitorChargeModalState = atomWithReset<ChargeModalState>({
  isOpen: false,
  appointmentId: null,
});

export const lineVisitorNotificationConfirmModalState =
  atomWithReset<NotificationConfirmModalState>({
    isOpen: false,
    appointmentId: null,
    lineVisitorId: null,
    webVisitorId: null,
  });

export const lineVisitorNotificationCongestionModalState =
  atomWithReset<NotificationCongestionModalState>({
    isOpen: false,
    appointmentId: null,
    lineVisitorId: null,
    webVisitorId: null,
  });

export const lineVisitorSendUnavailableModalState = atomWithReset<SendUnavailableModalState>({
  isOpen: false,
});

export const webVisitorProfileDrawerState = atomWithReset<WebVisitorProfileDrawerState>({
  isOpen: false,
  webVisitorId: null,
  appointmentId: null,
});

export const webVisitorTabState = atomWithReset<WebVisitorTab>({
  type: 'reception',
});

export const webVisitorFileState = atom<WebVisitorFileState>({
  webVisitorId: null,
});

export const webVisitorChargeModalState = atomWithReset<ChargeModalState>({
  isOpen: false,
  appointmentId: null,
});

export const webVisitorNotificationConfirmModalState = atomWithReset<NotificationConfirmModalState>(
  {
    isOpen: false,
    appointmentId: null,
    lineVisitorId: null,
    webVisitorId: null,
  },
);

export const webVisitorNotificationCongestionModalState =
  atomWithReset<NotificationCongestionModalState>({
    isOpen: false,
    appointmentId: null,
    lineVisitorId: null,
    webVisitorId: null,
  });

export const checkinEntryProfileDrawerState = atomWithReset<CheckinEntryProfileDrawerState>({
  isOpen: false,
  checkinEntryId: null,
});

export const outpatientQuestionnaireEntryProfileDrawerState =
  atomWithReset<OutpatientQuestionnaireEntryProfileDrawerState>({
    isOpen: false,
    outpatientQuestionnaireEntryId: null,
  });

export const outpatientQuestionnaireNotifyConfirmModalState =
  atomWithReset<OutpatientQuestionnaireNotifyConfirmModalState>({
    isOpen: false,
    outpatientQuestionnaireEntryId: null,
  });

export const outpatientQuestionnaireSendUnavailableModalState =
  atomWithReset<OutpatientQuestionnaireEntrySendUnavailableModalState>({
    isOpen: false,
  });

export const outpatientQuestionnaireFinishConfirmModalModalState =
  atomWithReset<OutpatientQuestionnaireEntryFinishConfirmModalModalState>({
    isOpen: false,
    outpatientQuestionnaireEntryId: null,
  });

export const draftAppointmentPatientProfileDrawerState =
  atomWithReset<DraftAppointmentPatientProfileDrawerState>({
    isOpen: false,
    draftAppointmentId: null,
    patientId: null,
    error: null,
  });

export const cancelDraftAppointmentPane = atomWithReset<CancelDraftAppointmentPaneState>({
  isOpen: false,
});

export const requestAppointmentModalState = atomWithReset<RequestAppointmentModalState>({
  isOpen: false,
});

export const finishDraftAppointmentModalState = atomWithReset<FinishDraftAppointmentModalState>({
  isOpen: false,
});

export const requestUberDeliveryModalState = atomWithReset<RequestUberDeliveryModalState>({
  isOpen: false,
  step: 'checkList',
  appointmentId: null,
  quoteId: null,
  error: null,
  graphqlError: null,
  autoMode: null,
  checked: false,
});
